import { useCallback, useMemo, useState } from 'react'
import type { DataFile } from 'shared/types/datafile-type'
import useUploadFile from 'modules/attachments/hooks/use-upload-datafile'

interface UploadFileInterface {
  file: File
  source?: string
}

interface UseUploadImageResult {
  uploadImage: ({ file, source }: UploadFileInterface) => Promise<DataFile>
  isFetching: boolean
}

export function useUploadImage(): UseUploadImageResult {
  const { validateAndGetUploadOptions, uploadFile, activateFile } = useUploadFile()
  const [isValidating, setIsValidating] = useState(false)
  const [isActivating, setIsActivating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const uploadImage = useCallback(
    async ({ file, source = 'community' }: UploadFileInterface) => {
      // 1) Validate and get S3 options
      setIsValidating(true)
      const { id, uploadOptions } = await validateAndGetUploadOptions({
        file: { name: file.name, size: file.size, type: file.type },
        source,
      }).finally(() => setIsValidating(false))

      // 2) Upload to S3
      setIsUploading(true)
      await uploadFile({ file, uploadOptions }).finally(() => setIsUploading(false))

      // 3) Activate file and adapt to DataFile shape
      setIsActivating(true)
      const activated = await activateFile(id).finally(() => setIsActivating(false))
      const info = activated[id]
      const dataFile: DataFile = {
        id: info.id,
        path: info.path,
        filename: info.filename,
        name: info.name || info.filenameWithoutHash || info.filename,
        thumbnail: info.thumbnail || null,
      }
      return dataFile
    },
    [activateFile, validateAndGetUploadOptions, uploadFile],
  )

  const isFetching = useMemo(
    () => isUploading || isValidating || isActivating,
    [isUploading, isValidating, isActivating],
  )

  return {
    uploadImage,
    isFetching,
  }
}
